import React from 'react';

// Xizmat qatori - xizmat zanjiri bo'ylab bir xil tuzilma (Case -> taklif ->
// shartnoma -> dalolatnoma). Qarang backend/app/core/service_lines.py.
export interface ServiceLine {
  product_id?: number | null;
  name: string;
  name_ru?: string | null;
  name_en?: string | null;
  quantity: number;
  unit?: string | null;
  price?: number | null;
  instrument_id?: number | null;
  equipment_name?: string | null;
  equipment_serial?: string | null;
  equipment_category?: string | null;
  warranty?: string | null;          // eski erkin matn (parse qilinmagan) - faqat ko'rsatiladi
  warranty_value?: number | null;    // kafolat: son + birlik
  warranty_unit?: string | null;     // "month" | "day"
}

// "6 oy" / "10 kun" - forma va ro'yxatlar uchun (hujjatda tilga qarab backend yozadi)
export const warrantyLabel = (l: { warranty?: string | null; warranty_value?: number | null; warranty_unit?: string | null }) =>
  l.warranty_value ? `${l.warranty_value} ${l.warranty_unit === 'day' ? 'kun' : 'oy'}` : (l.warranty || '');

export interface CatalogService {
  id: number;
  name: string;
  name_ru?: string;
  name_en?: string;
  unit?: string;
  sale_price?: number;
  product_type?: string;
}

export interface ClientInstrument {
  id: number;
  label: string;
  inventory_number?: string;
  serial_number?: string;
}

export const EMPTY_SERVICE_LINE: ServiceLine = {
  product_id: null, name: '', quantity: 1, unit: 'service_unit', price: null,
  instrument_id: null, equipment_name: '', equipment_serial: '', equipment_category: '', warranty: '',
  warranty_value: null, warranty_unit: 'month',
};

const UNIT_OPTIONS: Record<string, string> = {
  service_unit: 'Xizmat', hour: 'Soat', piece: 'Dona',
};

export const EQUIPMENT_CATEGORY_OPTIONS: Record<string, string> = {
  heating: 'Elektr isitish (1 oy)',
  electromechanical: 'Elektromexanik (2 oy)',
  electronic_optical: 'Elektron / optik (3 oy)',
};

// Backend'ga yuboriladigan shakl: bo'sh satrlar null, toifa bo'sh bo'lsa null.
export const serviceLinePayload = (l: ServiceLine) => ({
  product_id: l.product_id || null,
  name: l.name.trim(),
  name_ru: l.name_ru || null,
  name_en: l.name_en || null,
  quantity: Number(l.quantity) || 1,
  unit: l.unit || null,
  price: l.price === null || l.price === undefined || (l.price as any) === '' ? null : Number(l.price),
  instrument_id: l.instrument_id || null,
  equipment_name: l.equipment_name || null,
  equipment_serial: l.equipment_serial || null,
  equipment_category: l.equipment_category || null,
  warranty: l.warranty_value ? null : (l.warranty || null),
  warranty_value: l.warranty_value ? Number(l.warranty_value) : null,
  warranty_unit: l.warranty_value ? (l.warranty_unit || 'month') : null,
});

interface Props {
  lines: ServiceLine[];
  onChange: (lines: ServiceLine[]) => void;
  catalog: CatalogService[];
  // null = uskunani tanlab bo'lmaydi (masalan case hali saqlanmagan) - faqat erkin matn
  instruments: ClientInstrument[] | null;
  instrumentsHint?: string;
  priceLabel?: string;
  disabled?: boolean;
}

const inputCls = 'w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500';

const ServiceLinesEditor: React.FC<Props> = ({ lines, onChange, catalog, instruments, instrumentsHint, priceLabel, disabled }) => {
  const update = (idx: number, patch: Partial<ServiceLine>) =>
    onChange(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const remove = (idx: number) => onChange(lines.filter((_, i) => i !== idx));
  const add = () => onChange([...lines, { ...EMPTY_SERVICE_LINE }]);

  const pickCatalog = (idx: number, value: string) => {
    if (!value) { update(idx, { product_id: null }); return; }
    const s = catalog.find(c => c.id === Number(value));
    if (!s) return;
    update(idx, {
      product_id: s.id, name: s.name, name_ru: s.name_ru || null, name_en: s.name_en || null,
      unit: s.unit && UNIT_OPTIONS[s.unit] ? s.unit : lines[idx].unit,
      price: lines[idx].price ?? (s.sale_price || null),
    });
  };

  const pickInstrument = (idx: number, value: string) => {
    if (!value) { update(idx, { instrument_id: null }); return; }
    const ins = instruments?.find(i => i.id === Number(value));
    if (!ins) return;
    update(idx, { instrument_id: ins.id, equipment_name: ins.label, equipment_serial: ins.serial_number || '' });
  };

  return (
    <div className="space-y-2">
      {instruments === null && instrumentsHint && <p className="text-[11px] text-gray-400">{instrumentsHint}</p>}
      {lines.map((l, idx) => (
        <div key={idx} className="border rounded-lg p-2 bg-gray-50 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-4">
              <label className="block text-[11px] text-gray-500">Xizmat (katalogdan yoki erkin matn)</label>
              <select value={l.product_id || ''} disabled={disabled} onChange={e => pickCatalog(idx, e.target.value)} className={inputCls}>
                <option value="">— erkin matn —</option>
                {catalog.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input value={l.name} disabled={disabled} onChange={e => update(idx, { name: e.target.value })}
                placeholder="Ish / xizmat nomi" className={`${inputCls} mt-1`} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[11px] text-gray-500">Soni</label>
              <input type="number" min={0} step="any" value={l.quantity} disabled={disabled}
                onChange={e => update(idx, { quantity: e.target.value as any })} className={inputCls} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[11px] text-gray-500">Birlik</label>
              <select value={l.unit || ''} disabled={disabled} onChange={e => update(idx, { unit: e.target.value })} className={inputCls}>
                {Object.entries(UNIT_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-[11px] text-gray-500">{priceLabel || 'Narx (QQS ichida)'}</label>
              <input type="number" min={0} step="any" value={l.price ?? ''} disabled={disabled}
                onChange={e => update(idx, { price: e.target.value === '' ? null : (e.target.value as any) })} className={inputCls} />
            </div>
            <div className="md:col-span-1 flex items-end">
              {!disabled && <button type="button" onClick={() => remove(idx)} className="text-red-500 text-xs px-2 py-1 hover:bg-red-50 rounded">✕</button>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-4">
              <label className="block text-[11px] text-gray-500">Uskuna</label>
              {instruments !== null && (
                <select value={l.instrument_id || ''} disabled={disabled} onChange={e => pickInstrument(idx, e.target.value)} className={inputCls}>
                  <option value="">— erkin matn —</option>
                  {instruments.map(i => <option key={i.id} value={i.id}>{i.label}{i.serial_number ? ` (${i.serial_number})` : ''}</option>)}
                </select>
              )}
              <input value={l.equipment_name || ''} disabled={disabled || !!l.instrument_id}
                onChange={e => update(idx, { equipment_name: e.target.value })}
                placeholder="Uskuna nomi / modeli" className={`${inputCls} mt-1`} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[11px] text-gray-500">Seriya raqami</label>
              <input value={l.equipment_serial || ''} disabled={disabled || !!l.instrument_id}
                onChange={e => update(idx, { equipment_serial: e.target.value })} className={inputCls} />
            </div>
            <div className="md:col-span-3">
              <label className="block text-[11px] text-gray-500" title="Faqat ta'mirlash ishi uchun - 4.3-band kafolati toifaga qarab">Uskuna toifasi (ta'mirlash, 4.3-band)</label>
              <select value={l.equipment_category || ''} disabled={disabled} onChange={e => update(idx, { equipment_category: e.target.value })} className={inputCls}>
                <option value="">—</option>
                {Object.entries(EQUIPMENT_CATEGORY_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-[11px] text-gray-500">Xizmat kafolati (ixtiyoriy)</label>
              <div className="flex gap-1">
                <input type="number" min={1} max={600} value={l.warranty_value ?? ''} disabled={disabled}
                  onChange={e => (v => update(idx, { warranty_value: v }))(e.target.value === '' ? null : Number(e.target.value))}
                  placeholder="son" className={`${inputCls} w-20`} />
                <select value={l.warranty_unit || 'month'} disabled={disabled} onChange={e => (v => update(idx, { warranty_unit: v }))(e.target.value)} className={inputCls}>
                  <option value="month">oy</option><option value="day">kun</option>
                </select>
              </div>
              {l.warranty && !l.warranty_value && <p className="text-[11px] text-orange-600 mt-0.5">Eski matn: «{l.warranty}» - son kiritilsa almashtiriladi</p>}
            </div>
          </div>
        </div>
      ))}
      {!disabled && (
        <button type="button" onClick={add} className="text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded border border-dashed border-blue-300">
          + Xizmat qo'shish
        </button>
      )}
    </div>
  );
};

export default ServiceLinesEditor;
